import { fabric } from 'fabric';
import { isCustomTextboxBehaviorEnabled, TEXT_CONSTANTS } from './textbox-utils';
import { cleanAllStyles } from './textbox-styles';

export function overrideSplitTextIntoLines(): void {
  /**
   * Override of fabric.Textbox.prototype._splitTextIntoLines
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/shapes/textbox.class.ts#L421
   *
   * changes: passing maxWidth instead of width if the textbox has not been resized before, to support dynamic width
   */
  (fabric.Textbox.prototype as any)._splitTextIntoLines = function (text: string): any {
    // make the wrapWidth slightly larger than the maxWidth,
    // to avoid unneeded text wrapping when we set maxWidth to current width.
    // e.g. when change fontSize or fontFamily for the textbox
    const wrapWidth =
      (this.resized ? this.width : this.maxWidth ?? this.width) + TEXT_CONSTANTS.WRAP_WIDTH_MARGIN;

    const newText = (fabric.Text.prototype as any)._splitTextIntoLines.call(this, text || '');
    const graphemeLines = (fabric.Textbox.prototype as any)._wrapText.call(
      this,
      newText.lines,
      wrapWidth,
    );
    const lines = new Array(graphemeLines.length);
    for (let i = 0; i < graphemeLines.length; i++) {
      lines[i] = graphemeLines[i].join('');
    }
    newText.lines = lines;
    newText.graphemeLines = graphemeLines;
    return newText;
  };
}

/**
 * Goal: change the textbox width dynamically to fit the maximum line length until reaching the maxWidth
 * Changes:
 * - Override onInput, call handleDynamicTextboxWidth before emitting 'changed' event
 * - Override the actionHandler of textbox horizontal controls to set the resized flag after resizing
 */
export function enableDynamicTextboxWidth(): void {
  /**
   * Override of fabric.IText.prototype.onInput
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/mixins/itext_key_behavior.mixin.ts#L171
   *
   * changes:
   * - added handleDynamicTextboxWidth function to adjust width based on content
   * - calls handleDynamicTextboxWidth before firing 'changed' event
   * - add lastInputWasPaste flag to be used for handling styles of pasted text
   * - clean the styles for the textbox if we are pasting text to remove any redundant styles
   */
  (fabric.IText.prototype as any).onInput = function (e: any) {
    const handleDynamicTextboxWidth = () => {
      // skip if the textbox is resized manually
      if (this.resized) {
        return;
      }
      const maxLineWidth = getMaxLineWidth(this);
      const maxTextboxWidth = this.maxWidth ?? this.width;
      if (Math.abs(maxLineWidth - this.width) < TEXT_CONSTANTS.EPSILON) {
        return;
      }
      if (maxLineWidth < maxTextboxWidth) {
        this.width = maxLineWidth;
      }
    };

    const fromPaste = this.fromPaste;
    this.lastInputWasPaste = fromPaste;
    this.fromPaste = false;
    e && e.stopPropagation();
    if (!this.isEditing) {
      return;
    }
    // decisions about style changes.
    const nextText = this._splitTextIntoLines(this.hiddenTextarea.value).graphemeText;
    const charCount = this._text.length;
    const nextCharCount = nextText.length;
    let removedText;
    let charDiff = nextCharCount - charCount;
    const selectionStart = this.selectionStart;
    const selectionEnd = this.selectionEnd;
    const selection = selectionStart !== selectionEnd;
    let copiedStyle: any;
    let removeFrom;
    let removeTo;
    if (this.hiddenTextarea.value === '') {
      this.styles = {};
      this.updateFromTextArea();
      handleDynamicTextboxWidth();
      this.fire('changed');
      if (this.canvas) {
        this.canvas.fire('text:changed', { target: this });
        this.canvas.requestRenderAll();
      }
      return;
    }

    const textareaSelection = this.fromStringToGraphemeSelection(
      this.hiddenTextarea.selectionStart,
      this.hiddenTextarea.selectionEnd,
      this.hiddenTextarea.value,
    );
    const backDelete = selectionStart > textareaSelection.selectionStart;

    if (selection) {
      removedText = this._text.slice(selectionStart, selectionEnd);
      charDiff += selectionEnd - selectionStart;
    } else if (nextCharCount < charCount) {
      if (backDelete) {
        removedText = this._text.slice(selectionEnd + charDiff, selectionEnd);
      } else {
        removedText = this._text.slice(selectionStart, selectionStart - charDiff);
      }
    }
    const insertedText = nextText.slice(
      textareaSelection.selectionEnd - charDiff,
      textareaSelection.selectionEnd,
    );
    if (removedText && removedText.length) {
      if (insertedText.length) {
        // let's copy some style before deleting.
        // we want to copy the style before the cursor OR the style at the cursor if selection
        // is bigger than 0.
        copiedStyle = this.getSelectionStyles(selectionStart, selectionStart + 1, false);
        // now duplicate the style one for each inserted text.
        copiedStyle = insertedText.map(function () {
          // this return an array of references, but that is fine since we are
          // copying the style later.
          return copiedStyle[0];
        });
      }
      if (selection) {
        removeFrom = selectionStart;
        removeTo = selectionEnd;
      } else if (backDelete) {
        // detect differences between forwardDelete and backDelete
        removeFrom = selectionEnd - removedText.length;
        removeTo = selectionEnd;
      } else {
        removeFrom = selectionEnd;
        removeTo = selectionEnd + removedText.length;
      }
      this.removeStyleFromTo(removeFrom, removeTo);
    }
    if (insertedText.length) {
      if (
        fromPaste &&
        insertedText.join('') === fabric.copiedText &&
        !(fabric as any).disableStyleCopyPaste
      ) {
        copiedStyle = fabric.copiedTextStyle;
      }
      this.insertNewStyleBlock(insertedText, selectionStart, copiedStyle);
    }
    this.updateFromTextArea();
    // clean the styles for the textbox if we are pasting text to remove any redundant styles
    if (insertedText.length && fromPaste && !(fabric as any).disableStyleCopyPaste) {
      cleanAllStyles(this);
    }
    handleDynamicTextboxWidth();
    this.fire('changed');
    if (this.canvas) {
      this.canvas.fire('text:changed', { target: this });
      this.canvas.requestRenderAll();
    }
  };

  // changes: set resized flag to true when textbox is resized manually from horicontal controls
  const handleChangeWidth = function (
    eventData: MouseEvent,
    transformData: fabric.Transform,
    x: number,
    y: number,
  ): boolean {
    const widthChanged = (fabric as any).controlsUtils.changeWidth(eventData, transformData, x, y);
    const target = transformData.target;
    (target as any).resized = true;
    return widthChanged;
  };

  fabric.Textbox.prototype.controls.mr.actionHandler = handleChangeWidth;
  fabric.Textbox.prototype.controls.ml.actionHandler = handleChangeWidth;
}

// return max line width of a textbox
export const getMaxLineWidth = function (textbox: fabric.Textbox): number {
  if (!textbox) {
    return 0;
  }
  let maxWidth = 0;
  for (let i = 0; i < textbox._textLines.length; i++) {
    const lineWidth = textbox.measureLine(i).width;
    if (lineWidth > maxWidth) {
      maxWidth = lineWidth;
    }
  }
  return maxWidth;
};

// update textbox width to the max line width, and update maxWidth if needed
export const updateTextboxWidth = function (textbox: fabric.Textbox): void {
  if (!textbox || (textbox as any).resized) {
    return;
  }
  textbox.initDimensions();
  const maxLineWidth = getMaxLineWidth(textbox);
  textbox.set('width', maxLineWidth);
  if (maxLineWidth > (textbox as any).maxWidth) {
    (textbox as any).set('maxWidth', maxLineWidth);
  }
};

export const setInitialTextboxWidth = function (textbox: fabric.Textbox): void {
  if (!textbox) {
    return;
  }
  if (isCustomTextboxBehaviorEnabled()) {
    textbox.set('width', TEXT_CONSTANTS.INITIAL_TEXTBOX_WIDTH);
  } else {
    textbox.set('width', TEXT_CONSTANTS.OLD_INITIAL_TEXTBOX_WIDTH);
  }
};
